import { RULES_VERSION, mealWeight } from "./config.js";
import { eventStateFor, type ContextoDeMissao } from "./missoes.js";
import type {
  ChallengeConfig, DayKey, EventState, ExtraPoints, Plate, ScoreBreakdown, ScoreResult,
} from "./types.js";

/* ============================================================
   O motor de pontos

   Uma única definição de "quanto vale o placar". A tela usa para
   antecipar, o servidor usa para conceder. As duas chamam esta função.

   Três coisas somam pontos, e todas caem no mesmo `dayPoints` — por isso
   o total é sempre a soma dele, nunca um número guardado à parte:

     1. os pratos da pessoa, com peso de refeição, regra de repetida e
        bônus de sequência;
     2. o bônus dos eventos concluídos, no dia em que fecharam;
     3. os palpites cravados em pratos ALHEIOS, que chegam de fora porque
        não dá para derivá-los dos pratos desta pessoa.
   ============================================================ */

/** Um dia atrás do outro, contando para trás a partir de `from`. */
export function streakOn(days: ReadonlySet<DayKey>, from: DayKey): number {
  let n = 0;
  let cursor = from;
  while (days.has(cursor)) {
    n += 1;
    cursor = previousDay(cursor);
  }
  return n;
}

/** "2026-08-01" -> "2026-07-31". Aritmética de calendário, sem fuso. */
export function previousDay(day: DayKey): DayKey {
  const [y, m, d] = day.split("-").map(Number);
  if (!y || !m || !d) return "";
  const date = new Date(Date.UTC(y, m - 1, d - 1));
  return toDayKeyUTC(date);
}

export function toDayKeyUTC(date: Date): DayKey {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${date.getUTCFullYear()}-${p(date.getUTCMonth() + 1)}-${p(date.getUTCDate())}`;
}

/**
 * Pontos-base de um prato, antes do bônus de sequência.
 * `repeated` é o segundo prato da mesma refeição no mesmo dia: continua
 * publicável — a galera chuta preço e caloria nele — mas não soma de novo.
 */
export function platePoints(
  config: ChallengeConfig,
  input: { homemade: boolean; mealId: string | null; repeated?: boolean },
): number {
  if (input.repeated) return config.rules.repeatMeal;
  const base = input.homemade ? config.rules.homemade : config.rules.bought;
  return base * mealWeight(config, input.mealId);
}

/**
 * Quanto um prato JÁ GRAVADO vale.
 * Prato de era anterior mantém o valor congelado na publicação — foi a
 * regra combinada quando a pontuação virou configurável, e é o que impede
 * uma edição de hoje mexer no placar de semanas atrás.
 */
export function storedPlatePoints(
  config: ChallengeConfig,
  plate: Plate,
  repeated: boolean,
): number {
  if (plate.rulesVersion >= RULES_VERSION) {
    return platePoints(config, { homemade: plate.homemade, mealId: plate.mealId, repeated });
  }
  return plate.storedBase ?? (plate.homemade ? config.rules.homemade : config.rules.bought);
}

export function applyStreak(config: ChallengeConfig, base: number, streak: number): number {
  return streak >= config.rules.streakFrom ? base * config.rules.streakMultiplier : base;
}

/** Agrupa por dia e ordena cada dia por horário. */
function byDay(plates: readonly Plate[]): Map<DayKey, Plate[]> {
  const map = new Map<DayKey, Plate[]>();
  for (const plate of plates) {
    if (!plate.day) continue;
    const list = map.get(plate.day);
    if (list) list.push(plate);
    else map.set(plate.day, [plate]);
  }
  for (const list of map.values()) list.sort((a, b) => a.at - b.at);
  return map;
}

/* O progresso das missões mora em `missoes.ts`, e é reexportado daqui
   porque é aqui que as telas antigas o procuram. */
export { eventStateFor };

/**
 * A conta completa de um membro.
 *
 * `extra` são os pontos que vieram de fora (cravadas). Eles não podem ser
 * recalculados aqui porque nasceram nos pratos de outras pessoas — quem os
 * guarda é a ficha do membro, e este motor apenas os soma de volta.
 */
export function scoreMember(
  config: ChallengeConfig,
  plates: readonly Plate[],
  options: { jokerDays?: readonly DayKey[]; extra?: ExtraPoints } & ContextoDeMissao = {},
): ScoreResult {
  const grouped = byDay(plates);
  const days = [...grouped.keys()].sort();
  const dayPoints: Record<DayKey, number> = {};

  // Vale-falta conta como dia cumprido para efeito de sequência.
  const streakDays = new Set<DayKey>([...days, ...(options.jokerDays ?? [])]);

  for (const day of days) {
    const seenMeals = new Set<string>();
    let sum = 0;
    for (const plate of grouped.get(day)!) {
      const repeated = !!plate.mealId && seenMeals.has(plate.mealId);
      if (plate.mealId) seenMeals.add(plate.mealId);
      sum += valorDoPrato(config, plate, repeated, streakOn(streakDays, day));
    }
    dayPoints[day] = sum;
  }

  const events: Record<string, EventState> = {};
  for (const event of config.events) {
    const state = eventStateFor(event, plates, options);
    events[event.id] = state;
    if (state.bonus > 0 && state.day) {
      dayPoints[state.day] = (dayPoints[state.day] ?? 0) + state.bonus;
    }
  }

  for (const [day, value] of Object.entries(options.extra ?? {})) {
    if (!value) continue;
    dayPoints[day] = (dayPoints[day] ?? 0) + value;
  }

  const homemadeCount = plates.filter((p) => p.homemade).length;

  return {
    days,
    dayPoints,
    total: round2(Object.values(dayPoints).reduce((a, b) => a + b, 0)),
    events,
    cuisineIds: [...new Set(plates.map((p) => p.cuisineId).filter((c): c is string => !!c))],
    homemadeCount,
    boughtCount: plates.length - homemadeCount,
    plateCount: plates.length,
  };
}

/**
 * A mesma conta, aberta para a tela explicar de onde veio cada ponto.
 *
 * A primeira versão disto, no app antigo, tirava o bônus por subtração
 * (`guardado − base`) e mentia: qualquer defasagem no placar aparecia como
 * "bônus de sequência" para quem nunca chegou perto de sete dias seguidos.
 * Aqui cada parcela é somada pelo mesmo caminho que a concede.
 */
export function breakdown(
  config: ChallengeConfig,
  plates: readonly Plate[],
  options: { jokerDays?: readonly DayKey[]; extra?: ExtraPoints } & ContextoDeMissao & {
    /**
     * O estado das missões já gravado pelo servidor na ficha. Quando vem,
     * é ele que conta: as missões de atividade e as coletivas dependem de
     * coisas que a tela não enxerga (os pratos do grupo inteiro, os
     * comentários da pessoa), e recalcular aqui daria outro número.
     */
    eventosGravados?: Record<string, EventState>;
  } = {},
): ScoreBreakdown {
  const grouped = byDay(plates);
  const streakDays = new Set<DayKey>([...grouped.keys(), ...(options.jokerDays ?? [])]);

  let base = 0, baseHomemade = 0, baseBought = 0;
  let streakBonus = 0, repeated = 0, repeatedLoss = 0;
  let semFoto = 0;

  for (const [day, list] of grouped) {
    const seenMeals = new Set<string>();
    for (const plate of list) {
      const isRepeat = !!plate.mealId && seenMeals.has(plate.mealId);
      if (plate.mealId) seenMeals.add(plate.mealId);

      const value = storedPlatePoints(config, plate, isRepeat);
      if (isRepeat) {
        repeated += 1;
        repeatedLoss += storedPlatePoints(config, plate, false) - value;
      }
      base += value;
      // Separado prato a prato: com peso por refeição, dividir o total pela
      // proporção de pratos daria um número que não é de ninguém.
      if (plate.homemade) baseHomemade += value; else baseBought += value;
      streakBonus += applyStreak(config, value, streakOn(streakDays, day)) - value;
      if (plate.semFoto) semFoto += config.rules.semFotoCusto;
    }
  }

  const guessed = Object.values(options.extra ?? {}).reduce((a, b) => a + b, 0);
  const events = config.events
    .map((e) => (options.eventosGravados
      ? options.eventosGravados[e.id]?.bonus ?? 0
      : eventStateFor(e, plates, options).bonus))
    .reduce((a, b) => a + b, 0);

  return {
    base: round2(base),
    baseHomemade: round2(baseHomemade),
    baseBought: round2(baseBought),
    streakBonus: round2(streakBonus),
    guessed: round2(guessed),
    events: round2(events),
    repeated,
    repeatedLoss: round2(repeatedLoss),
    semFoto: round2(semFoto),
    total: round2(base + streakBonus + guessed + events - semFoto),
  };
}

/**
 * Quanto UM prato soma no dia: a base com o bônus de sequência, menos o
 * custo de ter vindo sem foto.
 *
 * O custo não é multiplicado pela sequência: é um valor fixo que a tela
 * promete antes de publicar ("−0,5"), e multiplicar faria a promessa
 * mudar conforme o dia.
 */
export function valorDoPrato(
  config: ChallengeConfig,
  plate: Plate,
  repeated: boolean,
  /** Tamanho da sequência no dia do prato (`streakOn`). */
  sequencia: number,
): number {
  const base = storedPlatePoints(config, plate, repeated);
  const comSequencia = applyStreak(config, base, sequencia);
  const custo = plate.semFoto ? config.rules.semFotoCusto : 0;
  return comSequencia - custo;
}

/** Pontos são múltiplos de 0,25; o ruído de ponto flutuante não é. */
export const round2 = (n: number): number => Math.round(n * 100) / 100;
